import React, { use, useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { CallWithAuth } from '../../../actions/apiAction';
import { CREATOR_EARNING_TREND } from '../../../utils/apiUrl';
import {
    getEarningsMetrics,
    peekEarningsMetrics,
} from '../../../utils/earningsCache';
import { Info } from '@mui/icons-material';

function formatEarningsLabel(item, granularity) {
    if (!item) return '';
    const { label, start_date } = item;

    if (granularity === 'day') {
        const d = new Date(`${start_date}T12:00:00`);
        return Number.isNaN(d.getTime())
            ? label
            : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }

    if (granularity === 'week') {
        const d = new Date(`${start_date}T12:00:00`);
        return Number.isNaN(d.getTime())
            ? label
            : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }

    if (granularity === 'month') {
        const d = new Date(`${start_date}T12:00:00`);
        return Number.isNaN(d.getTime())
            ? label
            : d.toLocaleDateString(undefined, { month: 'short' });
    }

    return label;
}


const EarningsAreaChart = ({ earningsData, loading }) => {
    const granularity = earningsData?.granularity;
    const chartData = earningsData?.data || [];

    // Show at most ~6 labels regardless of granularity/point count,
    // so weekly (14 pts) and daily (30/90 pts) views both stay readable.
    const maxTicks = 6;
    const tickInterval = chartData.length > maxTicks
        ? Math.ceil(chartData.length / maxTicks) - 1
        : 0;

    return (
        <div className="widget-body" style={{ width: '100%', height: 220 }}>
            {loading ? (
                <div className="chart-loading">Loading…</div>
            ) : (
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient id="weeklyEarningsGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#ec3fa0" stopOpacity={0.35} />
                                <stop offset="100%" stopColor="#b23ce0" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" vertical={false} />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: '#fff' }}
                            axisLine={false}
                            tickLine={false}
                            interval={tickInterval}
                            minTickGap={20}
                            tickFormatter={(_, index) =>
                                formatEarningsLabel(chartData[index], granularity)
                            }
                        />
                        <YAxis
                            tick={{ fontSize: 11, fill: '#fff' }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)}
                        />
                        <Tooltip
                            cursor={{ stroke: '#ec3fa0', strokeWidth: 1, strokeDasharray: '4 2' }}
                            formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Earnings']}
                            labelFormatter={(_, payload) =>
                                formatEarningsLabel(payload?.[0]?.payload, granularity)
                            }
                            contentStyle={{ background: '#000', border: 'none', borderRadius: 8, fontSize: 12 }}
                        />
                        <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#ec3fa0"
                            strokeWidth={2.5}
                            fill="url(#weeklyEarningsGradient)"
                            dot={false}
                            activeDot={{ r: 4, fill: '#ec3fa0', strokeWidth: 0 }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

const formatMoney = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '0.00';
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPercent = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '0';
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
};

const formatPayoutDate = (unixSeconds) => {
    if (!unixSeconds) return null;
    const d = new Date(Number(unixSeconds) * 1000);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const formatCalendarDate = (isoDate) => {
    if (!isoDate) return null;
    const [year, month, day] = String(isoDate).split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
    });
};

const HOLD_STATUS_LABEL = {
    in_hold: 'In hold',
    available: 'Available',
    paid: 'Paid',
    refunded: 'Refunded',
};

const LoadingSpinner = () => (
    <div
        className="w-10 h-10 rounded-full border-4 border-transparent border-t-[#AF0056] border-r-pink-400 animate-spin"
        aria-label="Loading"
    />
);

function HoldInfoButton({ paymentCount = 0, availableLabel }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const label = availableLabel
        ? `Held through the next calendar month to cover chargebacks. ${paymentCount === 1 ? 'This payment' : 'The next payment'} becomes available ${availableLabel}.`
        : 'Payments are held through the next calendar month to cover chargebacks, then move to available to pay.';

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open]);

    return (
        <span ref={wrapRef} className={`hold-info${open ? ' is-open' : ''}`}>
            <button
                type="button"
                className="hold-info__btn"
                aria-label="About the hold"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
            >
                <Info />
            </button>
            <span className="hold-info__tip" role="tooltip">
                {label}
            </span>
        </span>
    );
}

export default function MyEarnings() {
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(() => !peekEarningsMetrics());
    const [periodFilter, setPeriodFilter] = useState('30d');
    const [ballanceData, setBallanceData] = useState(() => peekEarningsMetrics());
    const loadBallance = useCallback(async () => {
        const cached = peekEarningsMetrics();
        if (cached && Object.prototype.hasOwnProperty.call(cached, 'next_available_date')) {
            setBallanceData(cached);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await getEarningsMetrics();
            setBallanceData(data);
        } catch (err) {
            console.error('Failed to load metrics summary', err);
        } finally {
            setLoading(false);
        }
    }, [])
    const loadEarnings = useCallback(async () => {

        // const cached = peekEarningsMetrics();
        // if (cached) {
        //     setSummary(cached);
        //     setLoading(false);
        //     return;
        // }
        setLoading(true);
        try {
            const data = await CallWithAuth('GET', CREATOR_EARNING_TREND(periodFilter));

            setSummary(data?.data);
        } catch (err) {
            console.error('Failed to load metrics summary', err);
        } finally {
            setLoading(false);
        }
    }, [periodFilter]);

    useEffect(() => {
        loadEarnings();
        loadBallance();
    }, [loadEarnings, loadBallance]);

    const formatLedgerDate = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return formatCalendarDate(iso.slice(0, 10)) || '—';
        return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    };

    const recentSubscriptions = ballanceData?.recent_subscriptions || [];
    return (
        <>
            <Helmet>
                <title>My Earnings</title>
            </Helmet>
            <div className="main-body">
                <div className="main-header-s1 center-text">
                    <button type="button" className="back-btn" onClick={() => navigate(-1)}>
                        <img src="/images/icons/chevron-left.svg" alt="" />
                    </button>
                    <h1 className="main-header-s1__title">My Earnings</h1>
                    {/* <button type="button" className="transparent-btn">
                        <img src="/images/icons/info.svg" alt="" />
                    </button> */}
                    <div className="main-header-s1__actions"></div>

                </div>
                <div className="body-content">
                    <div className="my-subscribers-layout">
                        {loading && !summary ? (
                            <div className="layout-item" style={{ width: '100%' }}>
                                <div
                                    className="layout-box widget-type2"
                                    style={{
                                        minHeight: 280,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <LoadingSpinner />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="layout-item">
                                    <div className="payoutHistory-information-card">
                                        <div className="top-info top-info--split">
                                            <div className="balance-col">
                                                <h3>TOTAL BALANCE</h3>
                                                {/* <h2>${formatMoney(ballanceData?.available_to_pay ?? ballanceData?.available_balance)}</h2> */}
                                                <h2>${formatMoney(ballanceData?.lifetime_earnings)}</h2>
                                            </div>
                                            {/* <div className="balance-col balance-col--next">
                                                <div className="balance-col-heading">
                                                    <h3>In hold</h3>
                                                    <HoldInfoButton
                                                        paymentCount={ballanceData?.held_count || 0}
                                                        availableLabel={
                                                            formatCalendarDate(ballanceData?.next_available_date)
                                                            || formatPayoutDate(ballanceData?.next_payout_date)
                                                        }
                                                        style={{ color: '#fff' }}
                                                    />
                                                </div>
                                                <h2>${formatMoney(ballanceData?.held_balance)}</h2>
                                                <p className="balance-caption">
                                                    {formatCalendarDate(ballanceData?.next_available_date)
                                                        || formatPayoutDate(ballanceData?.next_payout_date)
                                                        ? `Available ${formatCalendarDate(ballanceData?.next_available_date) || formatPayoutDate(ballanceData.next_payout_date)}`
                                                        : 'Held through next month'}
                                                </p>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>

                                {ballanceData && (
                                    <div className="layout-item">
                                        <div className="layout-box widget-type2">
                                            <div className="widget-header">
                                                <div className="left-div">
                                                    <h2>Earnings breakdown</h2>
                                                </div>
                                            </div>
                                            <div className="widget-body" style={{ paddingTop: 4 }}>
                                                <div className="earnings-breakdown">
                                                    <div className="earnings-breakdown__row">
                                                        <div className="earnings-breakdown__label">
                                                            <span className="earnings-breakdown__title">Subscriptions</span>
                                                            <span className="earnings-breakdown__hint">
                                                                {ballanceData.invoice_count
                                                                    ? `${ballanceData.invoice_count} paid invoice${ballanceData.invoice_count === 1 ? '' : 's'}`
                                                                    : `${ballanceData.subscriber_count ?? 0} fans`}
                                                                {ballanceData.avg_subscription_fee != null
                                                                    ? ` · avg $${formatMoney(ballanceData.avg_subscription_fee)}`
                                                                    : ''}
                                                            </span>
                                                        </div>
                                                        <span className="earnings-breakdown__value">
                                                            ${formatMoney(ballanceData.gross_subscription_revenue)}
                                                        </span>
                                                    </div>
                                                    <div className="earnings-breakdown__row">
                                                        <div className="earnings-breakdown__label">
                                                            <span className="earnings-breakdown__title">Platform fee</span>
                                                            {ballanceData.platform_fee_amount != null && (
                                                                <span className="earnings-breakdown__hint">
                                                                    ${formatMoney(ballanceData.platform_fee_amount)} per subscription
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="earnings-breakdown__value">
                                                            −${formatMoney(ballanceData.platform_fee_total)}
                                                        </span>
                                                    </div>
                                                    <div className="earnings-breakdown__row">
                                                        <div className="earnings-breakdown__label">
                                                            <span className="earnings-breakdown__title">Your share</span>
                                                            <span className="earnings-breakdown__hint">
                                                                {formatPercent(
                                                                    ballanceData.creator_split_percent
                                                                    ?? ballanceData.creator_share_percent
                                                                )}
                                                                % after platform fee
                                                            </span>
                                                        </div>
                                                        <span className="earnings-breakdown__value">
                                                            ${formatMoney(ballanceData.lifetime_earnings)}
                                                        </span>
                                                    </div>
                                                    <hr className="earnings-breakdown__divider" />
                                                    <div className="earnings-breakdown__row earnings-breakdown__row--total">
                                                        <span className="earnings-breakdown__title">Your earnings</span>
                                                        <span className="earnings-breakdown__value">
                                                            ${formatMoney(ballanceData.lifetime_earnings)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* {summary?.last_payout?.amount != null ? (
                                    <div className="layout-item">
                                        <div className="last-payout-card">
                                            <div className="last-payout-card__header">
                                                <h3>Last payout</h3>
                                                <span
                                                    className={`last-payout-card__status status-${summary.last_payout.status || 'paid'}`}
                                                >
                                                    {payoutStatusLabel(summary.last_payout.status)}
                                                </span>
                                            </div>
                                            <div className="last-payout-card__row">
                                                <span className="last-payout-card__label">Amount</span>
                                                <span className="last-payout-card__value">
                                                    ${formatMoney(summary.last_payout.amount)}
                                                </span>
                                            </div>
                                            <div className="last-payout-card__row">
                                                <span className="last-payout-card__label">Date</span>
                                                <span className="last-payout-card__value">
                                                    {formatPayoutDate(
                                                        summary.last_payout.arrival_date ||
                                                        summary.last_payout.created
                                                    ) || '—'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : summary ? (
                                    <div className="layout-item">
                                        <div className="last-payout-card last-payout-card--empty">
                                            <div className="last-payout-card__header">
                                                <h3>Last payout</h3>
                                            </div>
                                            <p className="last-payout-card__empty-text">
                                                No payouts yet. Once Stripe sends your first weekly
                                                transfer, it will show up here.
                                            </p>
                                        </div>
                                    </div>
                                ) : null} */}

                                {/* <div className="layout-item">
                                    <div className="layout-box widget-type2">
                                        <div className="widget-header">
                                            <div className="left-div">
                                                <h2>Earnings History</h2>
                                                <h4>Earnings over the past six weeks</h4>
                                            </div>
                                            <div className="period-tabs">
                                                {['30d', '90d', 'YTD'].map((p) => (
                                                    <button
                                                        key={p}
                                                        type="button"
                                                        className={`period-tab ${periodFilter === p ? 'active' : ''}`}
                                                        onClick={() => setPeriodFilter(p)}
                                                    >
                                                        {p}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="widget-body">
                                            <div className="earning-metrics-lists">
                                                <div className="earning-metrics-item">
                                                    <div className="earning-metrics-box">
                                                        <h4>Monthly</h4>
                                                        <h3>
                                                            $
                                                            {formatMoney(
                                                                summary?.earning_metrics?.monthly
                                                                    ?.amount
                                                            )}
                                                        </h3>
                                                    </div>
                                                </div>
                                                <div className="earning-metrics-item">
                                                    <div className="earning-metrics-box">
                                                        <h4>YTD</h4>
                                                        <h3>
                                                            $
                                                            {formatMoney(
                                                                summary?.earning_metrics?.ytd?.amount
                                                            )}
                                                        </h3>
                                                    </div>
                                                </div>
                                                <div className="earning-metrics-item">
                                                    <div className="earning-metrics-box">
                                                        <h4>TTM</h4>
                                                        <h3>
                                                            $
                                                            {formatMoney(
                                                                summary?.earning_metrics?.ttm?.amount
                                                            )}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div> */}

                                <div className="layout-item">
                                    <div className="layout-box widget-type2">
                                        <div className="widget-header">
                                            <div className="left-div">
                                                <h2>Earnings History</h2>
                                                {/* <h4>Earnings over the past six weeks</h4> */}
                                            </div>
                                            <div className="period-tabs pt-2">
                                                {['30d', '90d', 'ytd'].map((p) => (
                                                    <button
                                                        key={p}
                                                        type="button"
                                                        className={`period-tab ${periodFilter === p ? 'active' : ''}`}
                                                        onClick={() => setPeriodFilter(p)}
                                                    >
                                                        {p}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <EarningsAreaChart earningsData={summary} loading={loading} />
                                    </div>
                                </div>
                                <div className="layout-item">
                                    <div className="layout-box widget-type2">
                                        <div className="widget-header">
                                            <div className="left-div">
                                                <h2>Recent subscriptions</h2>
                                            </div>
                                        </div>
                                        <div className="widget-body">
                                            <div className="payout-history-lists">
                                                {recentSubscriptions.length === 0 ? (
                                                    <p>No subscriptions yet.</p>
                                                ) : (
                                                    recentSubscriptions.map((row) => (
                                                        <div className="payout-history-item" key={row.id}>
                                                            <div className="text-container">
                                                                <h3>${formatMoney(row.creator_amount)}</h3>
                                                                <ul>
                                                                    <li><strong>{row.fan_name || 'Subscription'}</strong></li>
                                                                    <li><span>{formatLedgerDate(row.created_at)}</span></li>
                                                                </ul>
                                                            </div>
                                                            <div className="status-div">
                                                                <div className={`status-tag ${row.status || 'in_hold'}`}>
                                                                    {HOLD_STATUS_LABEL[row.status] || 'In hold'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                </div>
            </div>
        </>
    );
}
